import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Box, Typography, TextField, Button, Modal, Paper } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LayoutUserHeader from '../components/LayoutUserHeader';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import numWords from 'num-words';
import { calculateInvoiceSummary, safeParseFloat } from '../utils/salesInvoiceCalculator';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const GetSaleInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);
  const [openAmendments, setOpenAmendments] = useState(false);
  const [selectedAmendments, setSelectedAmendments] = useState([]);

  const navigate = useNavigate();

  const handleAdd = () => navigate('/AddSaleInvoice');

  const handleOpenAmendments = (invoice) => {
    setSelectedAmendments(invoice.Amendments || []);
    setOpenAmendments(true);
  };

  const formatDDMMYYYY = (d) => {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
  };

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const gstNumber = localStorage.getItem('gstNumber');
      const res = await axios.get(`${BASE_URL}/SalesInvoice/List`, { headers: { Authorization: `Bearer ${token}` } });
      const raw = res.data.data || [];
      const filtered = gstNumber ? raw.filter(i => i.GSTNumber === gstNumber) : raw;
      const formatted = filtered.map((item, idx) => ({
        ...item, id: item._id, serialNumber: idx+1,
        InvoiceDate: item.InvoiceDate ? new Date(item.InvoiceDate).toLocaleDateString('en-IN',{day:'2-digit',month:'2-digit',year:'numeric'}) : ''
      }));
      setInvoices(formatted);
      setFilteredInvoices(formatted);
    } catch(e) { toast.error("Failed to fetch invoices"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchInvoices(); }, []);

  const handleSearch = (e) => {
    const v = e.target.value.toLowerCase();
    setSearch(v);
    setFilteredInvoices(invoices.filter(i => i?.InvoiceNumber?.toLowerCase().includes(v) || i?.CustomerName?.toLowerCase().includes(v)));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BASE_URL}/SalesInvoice/Delete/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Invoice deleted successfully");
      fetchInvoices();
    } catch(e) { toast.error("Delete failed"); }
  };

  const handleDownload = async (invoiceId) => {
    try {
      const token = localStorage.getItem("token");
      let invoice = null;
      let registration = {};

      try {
        const res = await axios.get(`${BASE_URL}/SalesInvoice/JoinWithRegistration/${invoiceId}`, { headers: { Authorization: `Bearer ${token}` } });
        invoice = res.data?.data?.invoice;
        registration = res.data?.data?.registration || {};
      } catch (joinErr) {
        console.warn("JoinWithRegistration failed, falling back to single invoice fetch:", joinErr);
        const resInvoice = await axios.get(`${BASE_URL}/SalesInvoice/Get/${invoiceId}`, { headers: { Authorization: `Bearer ${token}` } });
        invoice = resInvoice.data?.data || resInvoice.data;
      }

      if (!invoice) {
        toast.error("Invoice data could not be retrieved");
        return;
      }

      generateVectorPDF(invoice, registration);
    } catch(e) {
      console.error("Error generating invoice PDF:", e);
      toast.error("Failed to generate invoice PDF");
    }
  };

  // ---------------- NATIVE VECTOR PDF GENERATION ----------------
  const generateVectorPDF = (invoice = {}, registration = {}) => {
    const pdf = new jsPDF("p", "pt", "a4");
    const pdfW = pdf.internal.pageSize.getWidth(); // 595
    const pdfH = pdf.internal.pageSize.getHeight(); // 842
    const startX = 30;
    const contentWidth = 535;
    let currentY = 40;
    let pageNum = 1;

    const drawFooter = (pg) => {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(100);
      pdf.text(`Page ${pg}`, pdfW - 30, pdfH - 15, { align: "right" });
    };

    const checkPageOverflow = (neededHeight) => {
      if (currentY + neededHeight > 800) {
        pdf.addPage();
        pageNum++;
        drawFooter(pageNum);
        currentY = 40;
      }
    };

    drawFooter(pageNum);

    // ══ SECTION 1: SUPPLIER DETAILS + TAX INVOICE TITLE ══
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(1);
    pdf.rect(startX, currentY, contentWidth, 110);
    pdf.line(startX + contentWidth / 2, currentY, startX + contentWidth / 2, currentY + 110);

    // Left half: Supplier
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.setTextColor(0);
    pdf.text(registration?.firmName || '', startX + 8, currentY + 16);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(60);
    pdf.text(`Email: ${registration?.emailId || ''}   Mobile: ${registration?.mobileNumber || ''}`, startX + 8, currentY + 28);

    const supplierAddrLines = pdf.splitTextToSize(registration?.address || '', contentWidth / 2 - 16);
    pdf.text(supplierAddrLines, startX + 8, currentY + 40);

    const supplierY = currentY + 40 + (supplierAddrLines.length * 10) + 2;
    pdf.text(`State: ${registration?.stateName || ''} | Code: ${registration?.gst?.substring(0, 2) || ''}`, startX + 8, supplierY);
    pdf.text(`GSTIN: ${registration?.gst || ''}   PAN: ${registration?.gst?.substring(2, 12) || ''}`, startX + 8, supplierY + 12);

    // Right half: TAX INVOICE title & metadata
    pdf.setFillColor(212, 233, 253);
    pdf.rect(startX + contentWidth / 2 + 4, currentY + 4, contentWidth / 2 - 8, 18, "F");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(0);
    pdf.text(invoice?.InvoiceType === 'Bill of Supply' ? 'BILL OF SUPPLY' : 'TAX INVOICE', startX + contentWidth / 2 + contentWidth / 4, currentY + 16, { align: "center" });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(60);

    const colLeftX = startX + contentWidth / 2 + 8;
    const colRightX = startX + contentWidth / 2 + 140;

    pdf.text(`Invoice No: ${invoice?.InvoiceNumber || ''}`, colLeftX, currentY + 34);
    pdf.text(`Invoice Date: ${formatDDMMYYYY(invoice?.InvoiceDate)}`, colLeftX, currentY + 44);
    pdf.text(`Reverse Charge: ${invoice?.ReverseCharge || 'No'}`, colLeftX, currentY + 54);
    pdf.text(`Place of Supply: ${invoice?.PlaceOfSupply || ''}`, colLeftX, currentY + 64);

    pdf.text(`Transport Mode: ${invoice?.TransportationMode || ''}`, colRightX, currentY + 34);
    pdf.text(`Vehicle No: ${invoice?.VehicleNumber || ''}`, colRightX, currentY + 44);
    pdf.text(`Challan No: ${invoice?.ChallanNumber || ''}`, colRightX, currentY + 54);
    pdf.text(`Date of Supply: ${invoice?.DateOfSupply ? formatDDMMYYYY(invoice.DateOfSupply) : ''}`, colRightX, currentY + 64);

    pdf.rect(colLeftX, currentY + 76, 250, 26);
    pdf.line(colLeftX, currentY + 84, colLeftX + 250, currentY + 84);
    pdf.line(colLeftX, currentY + 93, colLeftX + 250, currentY + 93);
    pdf.text("[x] Original for Recipient", colLeftX + 4, currentY + 82);
    pdf.text("[ ] Duplicate for Transporter", colLeftX + 4, currentY + 91);
    pdf.text("[ ] Triplicate for Supplier", colLeftX + 4, currentY + 100);

    currentY += 110;

    // ══ SECTION 2: BILLED TO vs SHIPPED TO ══
    const billedAddrLines = pdf.splitTextToSize(invoice?.ReceiverDetails?.Address || '', contentWidth / 2 - 16);
    const shippedAddrLines = pdf.splitTextToSize(invoice?.ConsigneeDetails?.Address || '', contentWidth / 2 - 16);
    const maxAddrLines = Math.max(billedAddrLines.length, shippedAddrLines.length, 1);
    const section2Height = Math.max(90, 60 + (maxAddrLines * 10) + 15);

    pdf.rect(startX, currentY, contentWidth, section2Height);
    pdf.line(startX + contentWidth / 2, currentY, startX + contentWidth / 2, currentY + section2Height);

    // Left: Billed To
    pdf.setFont("helvetica", "bold");
    pdf.text("RECEIVER | BILLED TO", startX + 8, currentY + 12);
    pdf.setFont("helvetica", "normal");
    pdf.text(`Name: ${invoice?.ReceiverDetails?.Name || ''}`, startX + 8, currentY + 24);
    pdf.text(billedAddrLines, startX + 8, currentY + 34);

    const billedY = currentY + 34 + (billedAddrLines.length * 10);
    pdf.text(`GSTIN: ${invoice?.ReceiverDetails?.GSTNumber || ''}`, startX + 8, billedY);
    pdf.text(`State: ${invoice?.ReceiverDetails?.StateId || ''} | Code: ${invoice?.ReceiverDetails?.StateCode != null ? String(invoice.ReceiverDetails.StateCode).padStart(2, '0') : '--'}`, startX + 8, billedY + 10);
    pdf.text(`Place of Supply: ${invoice?.PlaceOfSupply || ''}`, startX + 8, billedY + 20);

    // Right: Shipped To
    pdf.setFont("helvetica", "bold");
    pdf.text("CONSIGNEE | SHIPPED TO", startX + contentWidth / 2 + 8, currentY + 12);
    pdf.setFont("helvetica", "normal");
    pdf.text(`Name: ${invoice?.ConsigneeDetails?.Name || ''}`, startX + contentWidth / 2 + 8, currentY + 24);
    pdf.text(shippedAddrLines, startX + contentWidth / 2 + 8, currentY + 34);

    const shippedY = currentY + 34 + (shippedAddrLines.length * 10);
    pdf.text(`GSTIN: ${invoice?.ConsigneeDetails?.GSTNumber || ''}`, startX + contentWidth / 2 + 8, shippedY);
    pdf.text(`State: ${invoice?.ConsigneeDetails?.StateId || ''} | Code: ${invoice?.ConsigneeDetails?.StateCode != null ? String(invoice.ConsigneeDetails.StateCode).padStart(2, '0') : '--'}`, startX + contentWidth / 2 + 8, shippedY + 10);

    currentY += section2Height + 10;

    // ══ SECTION 3: INVOICE DETAILS TABLE ══
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(0);
    pdf.text("Invoice Details:", startX, currentY);
    currentY += 8;

    const prods = invoice.Products || [];
    
    // Check State Code Routing (Intra-State vs Inter-State)
    const suppCode = registration?.gst ? registration.gst.substring(0, 2) : String(registration?.stateCode || '').padStart(2, '0');
    const recCode = (invoice?.ReceiverDetails?.StateCode != null && String(invoice.ReceiverDetails.StateCode) !== '0')
      ? String(invoice.ReceiverDetails.StateCode).padStart(2, '0')
      : (invoice?.ReceiverDetails?.GSTNumber ? invoice.ReceiverDetails.GSTNumber.substring(0, 2) : '');
    const consigneeCode = (invoice?.ConsigneeDetails?.StateCode != null && String(invoice.ConsigneeDetails.StateCode) !== '0')
      ? String(invoice.ConsigneeDetails.StateCode).padStart(2, '0')
      : (invoice?.ConsigneeDetails?.GSTNumber ? invoice.ConsigneeDetails.GSTNumber.substring(0, 2) : '');

    const destCode = consigneeCode || recCode;
    const isIntraState = (suppCode && destCode) ? (suppCode === destCode) : true;
    const isBOS = invoice.InvoiceType === 'Bill of Supply';

    let colDefs = [];
    if (isBOS) {
      colDefs = [
        { key: "sno", name: "S.No", align: "center", width: 22 },
        { key: "desc", name: "Description of\nGoods/Services", align: "left", isFlex: true },
        { key: "hsn", name: "HSN/SAC\nCode", align: "center", width: 45 },
        { key: "unit", name: "Unit", align: "center", width: 30 },
        { key: "qty", name: "Qty", align: "center", width: 32 },
        { key: "rate", name: "Rate\n(Rs.)", align: "right", width: 45 },
        { key: "amount", name: "Amount\n(Rs.)", align: "right", width: 55 },
        { key: "disc", name: "Disc\n(Rs.)", align: "right", width: 45 },
        { key: "total", name: "Total\n(Rs.)", align: "right", width: 60 }
      ];
    } else if (!isIntraState) {
      // Inter-State (IGST)
      colDefs = [
        { key: "sno", name: "S.No", align: "center", width: 20 },
        { key: "desc", name: "Description of\nGoods/Services", align: "left", isFlex: true },
        { key: "hsn", name: "HSN/SAC\nCode", align: "center", width: 40 },
        { key: "unit", name: "Unit", align: "center", width: 28 },
        { key: "qty", name: "Qty", align: "center", width: 30 },
        { key: "rate", name: "Rate\n(Rs.)", align: "right", width: 40 },
        { key: "amount", name: "Amount\n(Rs.)", align: "right", width: 45 },
        { key: "disc", name: "Disc\n(Rs.)", align: "right", width: 40 },
        { key: "taxable", name: "Taxable\n(Rs.)", align: "right", width: 48 },
        { key: "igstPct", name: "IGST\n(%)", align: "center", width: 32 },
        { key: "igstAmt", name: "IGST\nAmt(Rs.)", align: "right", width: 48 },
        { key: "total", name: "Total\n(Rs.)", align: "right", width: 52 }
      ];
    } else {
      // Intra-State (CGST + SGST: 14 Columns)
      colDefs = [
        { key: "sno", name: "S.No", align: "center", width: 18 },
        { key: "desc", name: "Description of\nGoods/Services", align: "left", isFlex: true },
        { key: "hsn", name: "HSN/SAC\nCode", align: "center", width: 38 },
        { key: "unit", name: "Unit", align: "center", width: 26 },
        { key: "qty", name: "Qty", align: "center", width: 28 },
        { key: "rate", name: "Rate\n(Rs.)", align: "right", width: 36 },
        { key: "amount", name: "Amount\n(Rs.)", align: "right", width: 40 },
        { key: "disc", name: "Disc\n(Rs.)", align: "right", width: 36 },
        { key: "taxable", name: "Taxable\n(Rs.)", align: "right", width: 42 },
        { key: "cgstPct", name: "CGST\n(%)", align: "center", width: 28 },
        { key: "cgstAmt", name: "CGST\nAmt(Rs.)", align: "right", width: 38 },
        { key: "sgstPct", name: "SGST\n(%)", align: "center", width: 28 },
        { key: "sgstAmt", name: "SGST\nAmt(Rs.)", align: "right", width: 38 },
        { key: "total", name: "Total\n(Rs.)", align: "right", width: 48 }
      ];
    }

    // Calculate Flex Column Width
    let fixedSum = 0;
    colDefs.forEach(c => { if (!c.isFlex) fixedSum += c.width; });
    const flexCol = colDefs.find(c => c.isFlex);
    if (flexCol) {
      flexCol.width = Math.max(70, contentWidth - fixedSum);
    }

    // Draw Table Header
    const headerHeight = 22;
    pdf.setFillColor(240, 240, 240);
    pdf.rect(startX, currentY, contentWidth, headerHeight, "F");
    pdf.rect(startX, currentY, contentWidth, headerHeight);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(6.5);
    pdf.setTextColor(0);

    let curX = startX;
    colDefs.forEach((col) => {
      if (curX > startX) pdf.line(curX, currentY, curX, currentY + headerHeight);

      let textX = curX;
      if (col.align === "center") textX += col.width / 2;
      else if (col.align === "right") textX += col.width - 2;
      else textX += 2;

      const lines = col.name.split('\n');
      if (lines.length > 1) {
        pdf.text(lines[0], textX, currentY + 8, { align: col.align });
        pdf.text(lines[1], textX, currentY + 16, { align: col.align });
      } else {
        pdf.text(col.name, textX, currentY + 13, { align: col.align });
      }

      curX += col.width;
    });

    currentY += headerHeight;

    // Draw Table Product Rows & Calculate Clean Totals
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(6.5);

    let runningTotQty = 0;
    let runningTotGross = 0;
    let runningTotDisc = 0;
    let runningTotTaxable = 0;
    let runningTotCGST = 0;
    let runningTotSGST = 0;
    let runningTotIGST = 0;
    let runningTotGrand = 0;

    prods.forEach((p, idx) => {
      const qty = safeParseFloat(p.Quantity || p.qty);
      const rate = safeParseFloat(p.Rate || p.rate);
      const grossAmount = qty * rate;

      const discPerc = safeParseFloat(p.DiscountPercentage || p.discount);
      const discAmtPerUnit = safeParseFloat(p.DiscountAmountPerUnit || p.discountQty);
      const discTotalAmt = safeParseFloat(p.DiscountAmount || p.discountAmount);
      const discType = Number(p.DiscountType || 0);

      let discountVal = 0;

      if (discTotalAmt > 0 && discTotalAmt <= grossAmount) {
        discountVal = discTotalAmt;
      } else if (discType === 0 && discPerc > 0) {
        discountVal = (grossAmount * discPerc) / 100;
      } else if (discType === 1 && discAmtPerUnit > 0) {
        if (discAmtPerUnit <= rate && discAmtPerUnit > 0) {
          discountVal = qty * discAmtPerUnit;
        } else {
          discountVal = discAmtPerUnit;
        }
      } else if (discPerc > 0) {
        discountVal = (grossAmount * discPerc) / 100;
      } else if (discAmtPerUnit > 0) {
        if (discAmtPerUnit <= rate && discAmtPerUnit > 0) {
          discountVal = qty * discAmtPerUnit;
        } else {
          discountVal = discAmtPerUnit;
        }
      }

      // Safety Cap: Discount cannot exceed gross amount
      if (discountVal > grossAmount) {
        discountVal = grossAmount;
      }

      const taxable = Math.max(0, grossAmount - discountVal);

      // Extract GST Rate from all potential stored fields
      const pIgst = safeParseFloat(p.IGSTPercentage);
      const pCgst = safeParseFloat(p.CGSTPercentage);
      const pSgst = safeParseFloat(p.SGSTPercentage);
      const pRate = safeParseFloat(p.GSTRate || p.GstPercentage || p.gstPercentage);

      let productGstRate = pRate;
      if (productGstRate === 0) {
        if (pIgst > 0) productGstRate = pIgst;
        else if (pCgst > 0 || pSgst > 0) productGstRate = pCgst + pSgst;
      }

      let cgstPct = 0, sgstPct = 0, igstPct = 0;
      let cgstAmt = 0, sgstAmt = 0, igstAmt = 0;

      if (isIntraState) {
        cgstPct = productGstRate > 0 ? (productGstRate / 2) : pCgst;
        sgstPct = productGstRate > 0 ? (productGstRate / 2) : pSgst;
        cgstAmt = (taxable * cgstPct) / 100;
        sgstAmt = (taxable * sgstPct) / 100;
      } else {
        igstPct = productGstRate > 0 ? productGstRate : pIgst;
        igstAmt = (taxable * igstPct) / 100;
      }

      const total = taxable + cgstAmt + sgstAmt + igstAmt;

      // Accumulate Totals
      runningTotQty += qty;
      runningTotGross += grossAmount;
      runningTotDisc += discountVal;
      runningTotTaxable += taxable;
      runningTotCGST += cgstAmt;
      runningTotSGST += sgstAmt;
      runningTotIGST += igstAmt;
      runningTotGrand += total;

      const descCol = colDefs.find(c => c.key === "desc");
      const wrappedDesc = pdf.splitTextToSize(p.ProductId || p.ProductName || '', descCol.width - 4);
      const rowHeight = Math.max(16, wrappedDesc.length * 8 + 4);

      checkPageOverflow(rowHeight);
      pdf.rect(startX, currentY, contentWidth, rowHeight);

      let rowX = startX;
      colDefs.forEach((col) => {
        if (rowX > startX) pdf.line(rowX, currentY, rowX, currentY + rowHeight);

        let cellText = "";
        if (col.key === "sno") cellText = String(idx + 1);
        else if (col.key === "desc") cellText = wrappedDesc;
        else if (col.key === "hsn") cellText = p.HsnSacCode || p.HSNCode || '';
        else if (col.key === "unit") cellText = p.UnitId || p.Unit || '';
        else if (col.key === "qty") cellText = qty > 0 ? qty.toFixed(2) : '0';
        else if (col.key === "rate") cellText = rate > 0 ? rate.toFixed(2) : '0.00';
        else if (col.key === "amount") cellText = grossAmount.toFixed(2);
        else if (col.key === "disc") cellText = discountVal.toFixed(2);
        else if (col.key === "taxable") cellText = taxable.toFixed(2);
        else if (col.key === "cgstPct") cellText = cgstPct > 0 ? `${cgstPct.toFixed(1)}%` : '0%';
        else if (col.key === "cgstAmt") cellText = cgstAmt.toFixed(2);
        else if (col.key === "sgstPct") cellText = sgstPct > 0 ? `${sgstPct.toFixed(1)}%` : '0%';
        else if (col.key === "sgstAmt") cellText = sgstAmt.toFixed(2);
        else if (col.key === "igstPct") cellText = igstPct > 0 ? `${igstPct.toFixed(1)}%` : '0%';
        else if (col.key === "igstAmt") cellText = igstAmt.toFixed(2);
        else if (col.key === "total") cellText = total.toFixed(2);

        let textX = rowX;
        if (col.align === "center") textX += col.width / 2;
        else if (col.align === "right") textX += col.width - 2;
        else textX += 2;

        if (col.key === "desc") {
          pdf.text(cellText, textX, currentY + 10);
        } else {
          pdf.text(cellText, textX, currentY + rowHeight / 2 + 2, { align: col.align });
        }

        rowX += col.width;
      });

      currentY += rowHeight;
    });

    // Draw Totals Row
    checkPageOverflow(18);
    pdf.setFillColor(245, 245, 245);
    pdf.rect(startX, currentY, contentWidth, 18, "F");
    pdf.rect(startX, currentY, contentWidth, 18);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(6.5);
    let totX = startX;

    colDefs.forEach((col) => {
      if (totX > startX) pdf.line(totX, currentY, totX, currentY + 18);

      let cellText = "";
      if (col.key === "sno" || col.key === "desc") {
        if (col.key === "sno") cellText = "Total";
      }
      else if (col.key === "qty") cellText = runningTotQty.toFixed(2);
      else if (col.key === "amount") cellText = runningTotGross.toFixed(2);
      else if (col.key === "disc") cellText = runningTotDisc.toFixed(2);
      else if (col.key === "taxable") cellText = runningTotTaxable.toFixed(2);
      else if (col.key === "cgstAmt") cellText = runningTotCGST.toFixed(2);
      else if (col.key === "sgstAmt") cellText = runningTotSGST.toFixed(2);
      else if (col.key === "igstAmt") cellText = runningTotIGST.toFixed(2);
      else if (col.key === "total") cellText = runningTotGrand.toFixed(2);

      let textX = totX;
      if (col.align === "center") textX += col.width / 2;
      else if (col.align === "right") textX += col.width - 2;
      else textX += 2;

      pdf.text(cellText, textX, currentY + 11, { align: col.align });
      totX += col.width;
    });

    currentY += 28;

    // ══ SECTION 4: TOTAL INVOICE VALUE IN WORDS ══
    checkPageOverflow(50);

    const safeToWords = (num) => {
      try {
        const val = Math.round(Number(num) || 0);
        if (!val || val <= 0) return 'Zero';
        return numWords(val);
      } catch (e) {
        return String(num || 0);
      }
    };

    const roundedGrandTotal = Math.round(runningTotGrand);
    const wordsStr = safeToWords(roundedGrandTotal);
    const formattedWords = wordsStr.charAt(0).toUpperCase() + wordsStr.slice(1) + " Rupees Only";

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(0);
    pdf.text("Total Invoice Value (in words):", startX, currentY);

    pdf.setFont("helvetica", "normal");
    pdf.text(formattedWords, startX, currentY + 14);

    currentY += 35;

    // ══ SECTION 5: BANK DETAILS, TERMS & SIGNATURE ══
    checkPageOverflow(120);

    pdf.rect(startX, currentY, contentWidth, 110);
    pdf.line(startX + contentWidth / 2, currentY, startX + contentWidth / 2, currentY + 110);

    // Left half: Bank & Terms
    const leftX = startX + 8;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.text("Bank Account Details:", leftX, currentY + 12);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    const acHolder = registration?.accountHolderName || registration?.firmName || 'NEW HINDUSTAN TRADERS';
    const acNo = registration?.accountNumber || '44513428379';
    const ifsc = registration?.ifscCode || 'SBIN0006034';
    const bankNameStr = (registration?.bankName && registration?.bankName.trim() !== '')
      ? `${registration.bankName} ${registration.bankBranch ? `(${registration.bankBranch})` : ''}`
      : 'STATE BANK OF INDIA (ADB BRANCH, RAMPUR)';

    pdf.text(`A/C Holder: ${acHolder}`, leftX, currentY + 24);
    pdf.text(`Account No: ${acNo}`, leftX, currentY + 34);
    pdf.text(`IFSC: ${ifsc}`, leftX, currentY + 44);
    pdf.text(`Bank Name: ${bankNameStr}`, leftX, currentY + 54);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.text("Terms and Conditions:", leftX, currentY + 72);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.text("1. This is an electronically generated document.", leftX, currentY + 83);
    const jurisdiction = registration?.district?.districtName || 'Rampur';
    pdf.text(`2. All disputes are subject to ${jurisdiction} jurisdiction only.`, leftX, currentY + 93);

    // Right half: Authorised Signatory
    const rightAlignX = startX + contentWidth - 8;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.text("Certified that the particulars given above are true and correct.", rightAlignX, currentY + 20, { align: "right" });

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8.5);
    const firmNameStr = registration?.firmName || 'NEW HINDUSTAN TRADERS';
    pdf.text(`For, ${firmNameStr}`, rightAlignX, currentY + 35, { align: "right" });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.text("Authorised Signatory", rightAlignX, currentY + 95, { align: "right" });

    // Save PDF
    const safeName = (invoice.InvoiceNumber || "invoice").toString().replace(/[^a-zA-Z0-9_-]/g, "_");
    pdf.save(`Invoice_${safeName}.pdf`);
  };

  const columns = [
    { field: 'serialNumber', headerName: 'S.No', flex: 1, sortable: false },
    { field: 'InvoiceNumber', headerName: 'Invoice Number', flex: 2 },
    { field: 'InvoiceDate', headerName: 'Invoice Date', flex: 2 },
    { field: 'actions', headerName: 'Actions', flex: 3, renderCell: (params) => (
      <>
        <Button variant="outlined" size="small" onClick={() => navigate(`/EditSaleInvoice/${params.row.id}`)}>Edit</Button>
        <Button variant="outlined" color="error" size="small" onClick={() => handleDelete(params.row.id)} style={{ marginLeft: 8 }}>Delete</Button>
        <Button variant="outlined" size="small" onClick={() => handleDownload(params.row.id)} style={{ marginLeft: 8 }}>Download</Button>
        <Button variant="outlined" color="secondary" size="small" onClick={() => handleOpenAmendments(params.row)} style={{ marginLeft: 8 }}>Amendments</Button>
      </>
    )}
  ];

  return (
    <>
      <LayoutUserHeader />
      <ToastContainer position="top-right" autoClose={3000} />
      <Modal open={openAmendments} onClose={() => setOpenAmendments(false)}>
        <Paper style={{ width: '80%', margin: '50px auto', padding: '20px', maxHeight: '200vh', overflowY: 'auto' }}>
          <Typography variant="h6" gutterBottom>Amendment History</Typography>
          {selectedAmendments.length === 0 ? <Typography>No amendments found.</Typography> : (
            <table width="100%" border="1" cellPadding="6" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f0f0f0' }}>
                  <th>#</th><th>Amended At</th><th>Modified By</th><th>Changes</th>
                </tr>
              </thead>
              <tbody>
                {selectedAmendments.map((amend, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{new Date(amend.amendedAt).toLocaleString()}</td>
                    <td>{amend.modifiedBy}</td>
                    <td>
                      <ul style={{ margin: 0, paddingLeft: 16 }}>
                        {Object.entries(amend.changes).map(([k, v], i) => (
                          <li key={i}>
                            <strong>{k}</strong>:&nbsp;
                            {typeof v === 'object' && v.old !== undefined
                              ? `Old: ${JSON.stringify(v.old)}, New: ${JSON.stringify(v.new)}`
                              : JSON.stringify(v)}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Paper>
      </Modal>
      <Box p={2}>
        <Typography variant="h5" gutterBottom>Sales Invoice List</Typography>
        <Box display="flex" justifyContent="space-between" mb={2}>
          <TextField label="Search" value={search} onChange={handleSearch} />
          <Button variant="contained" color="primary" onClick={handleAdd}>Add Invoice</Button>
          <Button variant="outlined" color="secondary" onClick={() => navigate('/ExcelUploader')}>Upload from Excel</Button>
        </Box>
        <DataGrid autoHeight rows={filteredInvoices} columns={columns} pageSize={5} rowsPerPageOptions={[5, 10]} loading={loading} getRowId={(row) => row.id} />
      </Box>
    </>
  );
};

export default GetSaleInvoices;
